<template>
  <v-container id="user_profile_root" fluid>
    <!-- DIALOG -->
    <router-view name="dialog"></router-view>
    <!-- CONTENT -->
    <v-container fluid>
      <v-row lass="mb-6 px-6" align="center" justify="center">
        <v-col>
          <span class="text-h5">
            {{ $t('users.title.profile') }}
            <v-spacer></v-spacer>
          </span>
        </v-col>
      </v-row>

      <v-row>
        <!-- SETTINGS -->
        <v-col>
          <v-card class="pa-6">
            <v-card-title color="black--text">
              <v-icon color="black" class="mr-2">mdi-account-outline</v-icon>
              {{ $t("users.settings.general") }}
            </v-card-title>
            <!-- <span class="text-h6">
              <v-icon color="black">mdi-account-outline</v-icon>
              {{ $t("users.settings.general") }}
            </span> -->
            <v-list-item>
              <v-list-item-content>
                <v-form ref="userProfileForm" v-model="userProfileFormValid">
                  <v-text-field
                    id="firstname"
                    v-model="user.firstName"
                    :label="$t('users.content.firstName')+ `*`"
                    :rules="[rules.required, rules.minMax]"
                  ></v-text-field>
                  <v-text-field
                    id="lastname"
                    v-model="user.lastName"
                    :label="$t('users.content.lastName')+ `*`"
                    :rules="[rules.required, rules.minMax]"
                  ></v-text-field>
                  <v-btn v-if="accountManagementURL" id="accountManagement" text color="primary" target="_blank" :href="accountManagementURL">
                    <v-icon class="mr-1">mdi-cog-outline</v-icon>
                    {{ $t('users.content.manageAccount') }}
                  </v-btn>
                  <v-btn
                    id="update"
                    class="mt-3 mb-3"
                    color="primary"
                    left
                    :disabled="updateMyUserDisabled"
                    @click="updateMyUser"
                  >
                    <v-icon>mdi-progress-pencil</v-icon>
                    {{ $t('main.update') }}
                  </v-btn>
                </v-form>
              </v-list-item-content>
            </v-list-item>

            <v-card-title color="black--text">
              <v-icon color="black" class="mr-2">mdi-map</v-icon>
              {{ $t('users.content.language') }}
            </v-card-title>
            <v-list-item>
              <v-list-item-content>
                <v-form ref="userLanguageForm">
                  <v-select
                    id="language"
                    v-model="selectedLanguage"
                    :items="languageOptions"
                    :label="$t('users.content.language')"
                    @change="onLanguageChange"
                  ></v-select>
                </v-form>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <div>
                  <span>{{ $t('users.content.roles.label') }}</span>
                  <v-chip-group column>
                    <v-chip v-for="role in rdssRoles" :key="role.value">{{ $t(role.text) }}</v-chip>
                  </v-chip-group>
                </div>
              </v-list-item-content>
            </v-list-item>

            <v-divider class="my-6"></v-divider>

            <v-card-title v-if="isVerificationEnabled" color="black--text">
              <v-icon color="black" class="mr-2">mdi-card-account-details</v-icon>

              {{ $t('users.verification.title') }}
            </v-card-title>

            <div v-if="isVerificationEnabled" id="verification-message" class="d-flex align-center" :class="userVerified ? 'green--text' : 'red--text'">
              <v-icon :color="userVerified ? 'success' : 'red'">{{ userVerified ? 'mdi-check' : 'mdi-close' }}</v-icon>
              <span>{{ userVerified ? $t('users.content.userIsVerified') : $t('users.content.userIsNotVerified') }}</span>
            </div>

            <v-data-table
              v-if="isVerificationEnabled"
              id="verification-table"
              :headers="verifiedHeaders"
              :items="verifiedFields"
              class="elevation-1 mt-3 mb-3 ml-3 mr-3"
              hide-default-footer
              :item-class="itemClass"
            >
              <template #items="props">
                <td :class="props.item.rowClass">{{ props.item.label }}</td>
                <td :class="props.item.rowClass">{{ props.item.value || 'N/A' }}</td>
              </template>
            </v-data-table>

            <v-btn
              v-if="isVerificationEnabled" id="reset-verification-button"
              :disabled="!userVerified"
              @click="resetVerification()"
            >
              <v-icon>mdi-restart</v-icon>
              {{ $t('users.verification.resetVerification') }}
            </v-btn>

            <template v-if="deleteAccountURL ">
              <v-divider v-if="isVerificationEnabled" class="my-6"></v-divider>
              <v-card-title color="black--text">
                <v-icon color="black" class="mr-2">mdi-close-octagon</v-icon>
                {{ $t('users.content.deleteMyAccount') }}
              </v-card-title>
              <v-card-subtitle class="ma-6 subtitle-1">
                {{ $t('users.content.descriptionDeleteMyAccount') }}
              </v-card-subtitle>

              <v-btn v-if="deleteAccountURL" id="deleteAccount" outlined text color="error" class="mb-3 mt-3" target="_blank" :href="deleteAccountURL">
                <v-icon class="mr-1">mdi-delete-outline</v-icon>
                {{ $t('users.content.deleteMyAccount') }}
              </v-btn>
            </template>
          </v-card>
        </v-col>

        <!-- <v-col v-if="!sealEnabled || showDevices"> -->
        <v-col v-if="showDevices">
          <v-card class="pa-6">
            <v-card-title>
              <v-icon color="black" class="mr-2">mdi-cellphone-key</v-icon>
              {{ $t('dashboard.devices') }}
            </v-card-title>

            <v-data-table
              id="devices_table"
              must-sort
              :headers="headers"
              hide-default-header
              hide-default-footer
              :items="deviceList"
              :options.sync="pagination"
              :server-items-length="devicesListTotalItems"
              :loading="dataTableLoading"
              :loading-text="$t('dataTable.content.loadingPleaseWait')"
              :no-data-text="$t('dataTable.content.noDataAvailable')"
            >
              <template #[`item.status`]="{item}">
                <v-chip
                  label
                  :color="getChipStyle(item)"
                >
                  {{ getChipText(item) }}
                </v-chip>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn
                  id="devices_table_manage"
                  text
                  class="pa-1 "
                  :to="'/user/profile/devices/' + item.id + '/update'"
                >
                  <v-icon>
                    mdi-update
                  </v-icon>
                  {{ $t('dataTable.content.devices.manage') }}
                </v-btn>

                <v-btn
                  id="devices_table_delete"
                  text
                  class="pa-1 "
                  @click="deleteDevice(item.id,$event)"
                >
                  <v-icon>
                    mdi-delete
                  </v-icon>
                  {{ $t('dataTable.content.devices.delete') }}
                </v-btn>
              </template>
            </v-data-table>

            <v-btn
              id="devices_table_add"
              text
              color="primary"
              to="/user/profile/devices/add"
            >
              <v-icon>mdi-plus</v-icon>
              {{ $t('dashboard.addDevice') }}
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import seal from '@/services/api/seal.js'
import debounce from 'lodash.debounce'
import { mapGetters } from 'vuex'
import Role from '../../Role'
import { showConfirmationMenu, showToastError, showToastSuccess } from '../../event-bus'
import credentials from '../../services/api/credentials'
import devices from '../../services/api/devices'
// import rdssUtil from '../../services/api/rdssUtil'
import userVerification from '../../services/api/userVerification'
import users from '../../services/api/users'
import rsspUtil from '../../services/api/rsspUtil'
import eventTypes from '../../eventTypes'
import CommonError from '../CommonError'
import language from './Language'
import UserStatus from './UserStatus'
import defaultRoles from './defaultRoles'

export default {
  name: 'UserProfile',

  data() {
    return {
      userProfileFormValid: false,
      maxCharacters: 255,
      user: {},
      // userVerificationStatus: null,
      selectedLanguage: language.EN,
      switchUserStatus: false,
      userStatusList: UserStatus,
      setDistinguishedName: false,
      distinguishedName: {},
      rules: {
        required: v => (!!v || !isNaN(parseFloat(v))) || this.$t('validation.form.requiredField'),
        minMax: v => (v && v.length <= this.maxCharacters) || this.$t('validation.form.mustBeLessThan', { max: this.maxCharacters }),
        email: v => !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || this.$t('validation.form.emailMustBeValid')
      },
      pagination: {
        sortBy: ['friendlyName']
      },
      deviceList: [],
      devicesListTotalItems: 0,
      sealStatus: {
        enabled: false,
        secureMode: false,
        activated: false
      },
      // sealActivated: this.$store.state.remoteConfig && this.$store.state.remoteConfig.sealSign,
      // showDevices: false,
      showDevices: true,
      // accountManagementURL: this.$store.state.remoteConfig && this.$store.state.remoteConfig.accountManagementURL,
      changePasswordURL: this.$store.state.remoteConfig && this.$store.state.remoteConfig.changePasswordURL,
      deleteAccountURL: this.$store.state.remoteConfig && this.$store.state.remoteConfig.deleteAccountURL,
      dataTableLoading: false,
      headers: [
        {
          text: this.$t('dataTable.header.devices.friendlyName'),
          align: 'left',
          value: 'friendlyName'
        },
        {
          text: this.$t('dataTable.header.devices.status'),
          align: 'left',
          value: 'status',
          sortable: false
        },
        {
          text: this.$t('dataTable.header.devices.user'),
          align: 'left',
          value: 'user.name'
        },
        {
          text: this.$t('dataTable.header.actions'),
          align: 'right',
          value: 'actions',
          sortable: false
        }
      ],
      rdssRoles: [],
      verifiedHeaders: [
        { text: this.$t('users.content.verifiedLabel'), value: 'label' },
        { text: this.$t('users.content.verifiedValue'), value: 'value' }
      ],
      userVerified: false,
      userVerificationStatus: {
        verified: false,
        fields: [

        ]
      }
    }
  },

  computed: {
    ...mapGetters(['hasRole']),
    isAdmin() {
      return this.hasRole(Role.ADMIN)
    },
    accountManagementURL() {
      if (!this.$store.state.remoteConfig ||
        !this.$store.state.remoteConfig.accountManagementURL) {
        return ''
      }
      const baseURL = this.$store.state.remoteConfig.accountManagementURL

      // Return empty string if base URL is empty
      if (!baseURL) {
        return ''
      }
      // Determine the isAdmin parameter value
      const isAdminParam = this.isAdmin ? 'isAdmin=1' : 'isAdmin=0'

      // Check if URL already has query parameters
      const separator = baseURL.includes('?') ? '&' : '?'

      return `${baseURL}${separator}${isAdminParam}`
    },
    languageOptions() {
      return [
        { value: language.EN, text: this.$t('users.content.languages.English') },
        { value: language.HU, text: this.$t('users.content.languages.Hungarian') }
      ]
    },

    sealActivated() {
      return this.sealStatus.enabled && this.sealStatus.activated
    },

    sealEnabled() {
      return this.sealStatus.enabled
    },
    hasRdss() {
      return !!this.$store.state.remoteConfig.rdssApiUrl
    },
    isVerificationEnabled() {
      return !!this.$store.state.remoteConfig.userVerificationEnabled
    },
    verifiedFields() {
      return this.userVerificationStatus.fields.map(field => {
        const rowClass = !field.optional && !field.value ? 'red--text red lighten-4' : ''
        return {
          label: field.displayName,
          value: field.value || null,
          rowClass
        }
      })
    },
    updateMyUserDisabled() {
      const { firstName, lastName } = this.$store.state.currentUser
      return this.user.firstName === firstName && this.user.lastName === lastName
    }
  },

  watch: {
    search() {
      this.dataTableLoading = true
      this.searchDeviceWatcher()
    },

    pagination() {
      this.searchDevices()
    }
  },

  async mounted() {
    this.sealStatus = await seal.getSealStatus()
    // removed sincse SIC new features
    /* if (this.sealStatus.enabled) {
      await this.checkCredentials()
    } else {
      this.showDevices = true
    } */
    await this.initDeviceEventSource()
    await this.getMyUser()
    await this.getVerificationDetails()
  },

  methods: {
    searchDeviceWatcher: debounce(function() {
      this.searchDevices()
    }, 500),

    async searchDevices() {
      this.dataTableLoading = true
      try {
        const pageRequest = {
          'page-num': this.pagination.page,
          'page-size': this.pagination.itemsPerPage,
          'sort-field': this.pagination.sortBy[0],
          'sort-order': this.pagination.sortDesc[0] ? 'desc' : 'asc'
        }

        const page = await devices.listMyDevices(this.search, pageRequest)
        this.deviceList = page.content
        this.devicesListTotalItems = page.total
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      } finally {
        this.dataTableLoading = false
      }
    },

    async onLanguageChange(newLanguage) {
      const localeRequest = {
        locale: newLanguage
      }

      try {
        await users.updateMyLocale(localeRequest)
        await rsspUtil.updateMyLocale(localeRequest)

        this.$nextTick(() => {
          this.setLanguage(newLanguage)
        })
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async updateMyUser() {
      if (!this.$refs.userProfileForm.validate()) {
        return
      }
      try {
        await users.updateMyUser(this.user)
        this.$store.commit('setCurrentUser', this.user)
        await rsspUtil.updateMyUser(this.user)

        showToastSuccess(this.$t('toastMessage.succesfullyUpdated'))
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async getMyUser() {
      try {
        this.user = await users.getMyUser()
        this.setDefaultValuesByGetMyUser()
        this.rdssRoles = this.user.roles.map(r => { return { value: r, text: defaultRoles.find(x => x.value === r).displayTextJsonKey } })
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async resetVerification() {
      try {
        this.userVerificationStatus = await userVerification.resetVerification()
        await this.getVerificationDetails()
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async getVerificationDetails() {
      if (!this.isVerificationEnabled) return
      try {
        /* const req = {
          transactionId: 'a3ca2b6d-b28e-442c-86d4-394e41d13a6d',
          processParam: {
            scenario: 'FullAuth'
          }
        }
        await userVerification.processRegula(req) */
        this.userVerificationStatus = await userVerification.getUserVerificationStatus()
        this.userVerified = this.userVerificationStatus.verified
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    itemClass(item) {
      return item.rowClass
    },

    setDefaultValuesByGetMyUser() {
      this.switchUserStatus = !!this.user.status.includes(UserStatus.ENABLED)
      this.selectedLanguage = this.user.locale
    },

    async logout() {
      await this.$auth.logout()
    },

    back() {
      this.$router.go(-1)
    },

    async deleteDevice(id, e) {
      const opts = {
        title: this.$t('dialog.confirmation.title'),
        posX: e.clientX,
        posY: e.clientY
      }
      const conf = await showConfirmationMenu(opts)
      if (!conf) {
        return
      }

      try {
        await devices.deleteDevice(id)
        this.searchDevices()
        showToastSuccess(this.$t('toastMessage.succesfullyDeleted'))
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if ((error.response !== undefined) && (error.response.data.error !== undefined)) {
          const splittedErrorMessage = error.response.data.error_description.split(' ')
          if (error.response.data.error === CommonError.STILL_REFERENCED) {
            errorMsg = this.$t('toastMessage.stillReferenced', { 0: splittedErrorMessage[0], 1: splittedErrorMessage[5] })
          } else {
            errorMsg = this.getErrorMessage(error)
          }
        } else {
          errorMsg = this.getErrorMessage(error)
        }
        showToastError(errorMsg)
      }
    },

    getChipStyle(item) {
      if (item.enabled === true) {
        return 'enabled'
      } else {
        return 'inactive'
      }
    },
    getChipText(item) {
      if (item.enabled === true) {
        return this.$t('dataTable.header.devices.states.enabled')
      } else {
        return this.$t('dataTable.header.devices.states.inactive')
      }
    },

    async checkCredentials() {
      const resp = await credentials.listMyActivatedCredentials()

      const total = resp.content.filter(elem => ((elem.keyPurpose === 'ASYMMETRIC_SIGN') && (elem.authMode === 'IMPLICIT'))).length
      this.showDevices = total > 0
      // this.showDevices = true
    },
    async initDeviceEventSource() {
      const token = await this.$auth.getIdToken()

      const eventSourceURL = this.$store.state.remoteConfig.rsspApiUrl + '/rssp/v1/events'
      let sse
      try {
        // eslint-disable-next-line
          sse = new EventSourcePolyfill(eventSourceURL, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
      } catch (e) {
        console.error(e)
      }

      sse.addEventListener(eventTypes.DEVICE_CREATED, (event) => {
        this.searchDeviceWatcher()
      })
      sse.addEventListener(eventTypes.DEVICE_UPDATED, (event) => {
        this.searchDeviceWatcher()
      })
      sse.addEventListener(eventTypes.DEVICE_DELETED, (event) => {
        this.searchDeviceWatcher()
      })
    }
  }
}
</script>

<style scoped>
.green--text {
  color: green !important;
}
.red--text {
  color: red !important;
}
</style>
